import { Icon } from '../components'
import type { CashflowGroup } from '../lib/api/types'
import {
  CashflowAmount,
  type CurrencyMode,
  type DrillCell,
  type GroupBy,
} from './cashflow-helpers'

type Props = {
  title: string
  items: CashflowGroup[]
  periods: string[]
  expanded: Set<string>
  groupBy: GroupBy
  currencyMode: CurrencyMode
  onToggle: (id: string) => void
  onDrill: (c: DrillCell) => void
  hasChildren: (id: string) => boolean
}

export function CashflowSection({
  title,
  items,
  periods,
  expanded,
  groupBy,
  currencyMode,
  onToggle,
  onDrill,
  hasChildren,
}: Props) {
  if (items.length === 0) return null
  return (
    <>
      <tr className="subtotal">
        <td colSpan={periods.length + (currencyMode === 'base' ? 2 : 1)}>
          <span style={{ fontWeight: 600, color: 'var(--fg-1)' }}>{title}</span>
        </td>
      </tr>
      {items.map((g) => {
        const canExpand = groupBy === 'category' && hasChildren(g.id)
        const isOpen = expanded.has(g.id)
        const indented = !!g.parentId
        return (
          <tr key={g.id}>
            <td className={indented ? 'indent' : ''}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {canExpand ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggle(g.id)
                    }}
                    style={{
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <Icon
                      name={isOpen ? 'chevron-down' : 'chevron-right'}
                      size={12}
                      color="var(--fg-3)"
                    />
                  </button>
                ) : (
                  <span style={{ width: 12, display: 'inline-block' }} />
                )}
                <span style={{ color: 'var(--fg-1)' }}>{g.name}</span>
              </span>
            </td>
            {periods.map((p) => {
              const v = g.totals[p]
              const empty =
                v === undefined ||
                v === 0 ||
                (typeof v === 'object' && Object.values(v).every((x) => x === 0))
              return (
                <td
                  key={p}
                  className="num"
                  style={{ cursor: empty ? 'default' : 'pointer' }}
                  onClick={() => {
                    if (!empty) onDrill({ group: g, periodKey: p })
                  }}
                >
                  <CashflowAmount v={v} />
                </td>
              )
            })}
            {currencyMode === 'base' && (
              <td className="num">
                <CashflowAmount v={g.totals._total} bold />
              </td>
            )}
          </tr>
        )
      })}
    </>
  )
}
